/**
 * `storeSamples.*` — the sampling queue (Commerce, M3).
 *
 * A sample is physical stock sent to a buyer *before* a bulk order, so they can
 * feel the fabric and check the colour. It carries value out of the building
 * against no order, which is why it is tracked at all.
 *
 * **Status vs phase.** The stored statuses are
 * `requested · dispatched · delivered · converted · returned`. `overdue` is
 * **derived** from `return_by` on read, never stored — the same treatment
 * `pastDispatch` gets on an order and `expiring` on a quote, and for the same
 * reason: a stored flag goes stale at midnight with nothing to flip it.
 *
 * Buyers can raise a request from the storefront (`buyerSamples.request`);
 * `buyerId` on the row says whether they did or whether staff raised it for
 * them. Everything after the request — dispatch, courier, return date — is the
 * seller's call, made here.
 *
 * @see packages/domain/src/commerce/store/sample-transitions.ts
 */
import { z } from "zod";
import { uuid, paged, moneyPaise } from "../_common.js";

/** Stored lifecycle states. `overdue` never appears here — see the docblock. */
export const sampleStatus = z.enum([
  "requested",
  "dispatched",
  "delivered",
  "converted",
  "returned",
]);
export type SampleStatus = z.infer<typeof sampleStatus>;

/** What the badge says: the stored status, or `overdue` once the clock says so. */
export const samplePhase = z.enum([
  "requested",
  "dispatched",
  "delivered",
  "converted",
  "returned",
  "overdue",
]);
export type SamplePhase = z.infer<typeof samplePhase>;

export const samplePurpose = z.enum(["evaluation", "photoshoot", "trade_show"]);

export const sampleRow = z.object({
  sampleId: uuid,
  /** Empty until dispatch acknowledges the request with a number. */
  docNumber: z.string(),
  status: sampleStatus,
  phase: samplePhase,
  purpose: samplePurpose,
  customerId: uuid,
  customerName: z.string(),
  /** Non-null when a storefront buyer asked, rather than staff. */
  buyerId: uuid.nullable(),
  /** Value in the buyer's hands — server-computed, never client-supplied. */
  valuePaise: moneyPaise,
  lineCount: z.number().int().nonnegative(),
  pieceCount: z.number().int().nonnegative(),
  requestedAt: z.string(),
  returnBy: z.string().nullable(),
  convertedOrderId: uuid.nullable(),
});

export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  q: z.string().optional(),
  status: sampleStatus.optional(),
});
export const listOutput = paged(sampleRow);

export const countsOutput = z.object({
  all: z.number().int().nonnegative(),
  requested: z.number().int().nonnegative(),
  dispatched: z.number().int().nonnegative(),
  delivered: z.number().int().nonnegative(),
  converted: z.number().int().nonnegative(),
  returned: z.number().int().nonnegative(),
  /** Recomputed from `return_by`, not counted off a column. */
  overdue: z.number().int().nonnegative(),
  /** Value still out: requested + dispatched + delivered. */
  outValuePaise: moneyPaise,
});

export const getInput = z.object({ sampleId: uuid });

export const sampleLine = z.object({
  lineId: uuid,
  position: z.number().int().positive(),
  /** Pieces, not packs — a sample is a handful to feel. */
  qty: z.number().int().positive(),
  returnExpected: z.boolean(),
  conditionNote: z.string().nullable(),
  listingId: uuid.nullable(),
  itemName: z.string().nullable(),
  sku: z.string().nullable(),
});

export const getOutput = sampleRow.extend({
  dispatchedAt: z.string().nullable(),
  returnedAt: z.string().nullable(),
  courier: z.string().nullable(),
  awb: z.string().nullable(),
  shipToText: z.string().nullable(),
  notesForBuyer: z.string().nullable(),
  lines: z.array(sampleLine),
});

export const transitionInput = z.object({
  sampleId: uuid,
  status: sampleStatus,
  /** Dispatch details. Ignored on any move other than `dispatched`. */
  courier: z.string().trim().max(64).optional(),
  awb: z.string().trim().max(64).optional(),
  /** ISO date the sample is due back — what makes `overdue` computable. */
  returnBy: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export const transitionOutput = z.object({ sampleId: uuid, status: sampleStatus });

export type ListSamplesInput = z.infer<typeof listInput>;
export type SampleDetail = z.infer<typeof getOutput>;
